import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, useTheme } from '@react-navigation/native';
import { SearchBar, Icon, DeleteModal, Header, BackButtonWeb, GenericSelectionModal, FeatureTooltip } from '@components';
import { useLanguage } from '@context';
import { Theme } from '@theme';
import ContactsFilter from '../components/ContactsFilter';
import { formatRecurrenceLabel, getCommonStyles, Scope, SelectionItem, type TranslateFn } from '@utils';
import { useCreationStackIntegration, useScreen, useExtension, useLoadAllPages, usePaginateKeepContacts, useDeleteKeepContact, KeepContact, useGetContactById, usePaginateHashtags, usePaginateCategories } from '@hooks';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MomentFilter, getMomentFilterMap } from '../components/MomentFilter';
import { groupByMoment } from '@utils';
import { defaultImage } from '@assets';
import { BoardView } from './components/BoardView';
import dayjs from 'dayjs';

type ReminderStatus = 'overdue' | 'today' | 'upcoming'

const getReminderStatus = (dateStr: string): ReminderStatus => {
  const now = dayjs()
  const date = dayjs(dateStr)
  if (date.isSame(now, 'day')) return 'today'
  if (date.isBefore(now, 'day')) return 'overdue'
  return 'upcoming'
}

export function formatReminderRepeatLabel(reminder: KeepContact, t: TranslateFn) {
  return formatRecurrenceLabel(reminder.rrule, reminder.keepDate, t);
}

export const KeepContactScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route: any = useRoute();
  const theme = useTheme();
  const { t } = useLanguage();
  const {isMobile, isDesktop, isWeb} = useScreen();
  const styles = getStyle(theme);
  const commonStyles = getCommonStyles(theme);
  const {isExtension} = useExtension()

  const contactId = route.params?.contactId;
  const {isCreationStackEnabled, showKeepContactCreation} = useCreationStackIntegration();
  const { mutate: deleteKeepContact } = useDeleteKeepContact();
  const { data: contact } = useGetContactById(contactId);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [searchText, setSearchText] = useState('');
  
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [reminderIdToDelete, setReminderIdToDelete] = useState<string | null>(null);

  const momentFilterMap = getMomentFilterMap(t)
  const [momentFilter, setMomentFilter] = useState<SelectionItem>({id: 'default', name: t('keepContact.filter')})
  
  const [filterSearchText, setFilterSearchText] = useState('')
  const [selectedHashtags, setSelectedHashtags] = useState<SelectionItem[]>([])
  const [selectedCategories, setSelectedCategories] = useState<SelectionItem[]>([])
  const [pendingHashtags, setPendingHashtags] = useState<SelectionItem[]>([])
  const [pendingCategories, setPendingCategories] = useState<SelectionItem[]>([])
  const [isHashtagModalVisible, setIsHashtagModalVisible] = useState(false)
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false)

  const { flatData: hashtags, isFetching: isFetchingHashtags, isFetchingNextPage: isFetchingNextPageHashtags, fetchNextPage: fetchNextPageHashtags, hasNextPage: hasNextPageHashtags } = usePaginateHashtags(filterSearchText)
  const { flatData: categories, isFetching: isFetchingCategories, isFetchingNextPage: isFetchingNextPageCategories, fetchNextPage: fetchNextPageCategories, hasNextPage: hasNextPageCategories } = usePaginateCategories(filterSearchText)

  const momentParam = momentFilter.id !== 'default' ? momentFilter.id as 'today' | 'week' | 'month' | 'later' : undefined;
  const scopeParam = scopes.length === 1 ? scopes[0] : undefined;
  const hashtagIds = selectedHashtags.map(h => h.id)
  const categoryIds = selectedCategories.map(c => c.id)
  
  const keepContactsQuery = usePaginateKeepContacts({
    search: searchText || undefined,
    scope: scopeParam,
    moment: momentParam,
    hashtagIds: hashtagIds.length > 0 ? hashtagIds : undefined,
    categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
  });
  const { flatData: keepContacts, totalCount } = keepContactsQuery;
  // The groups by period and the board need every follow-up.
  useLoadAllPages(keepContactsQuery);

  // Group by moment when no specific moment filter is selected
  const groupedByMoment = groupByMoment(keepContacts, 'keepDate')
  
  const statusColors: Record<ReminderStatus, string> = {
    overdue: theme.colors.error,
    today: '#FF9500',
    upcoming: theme.colors.green,
  }

  const statusLabels: Record<ReminderStatus, string> = {
    overdue: t('keepContact.statusLate'),
    today: t('common.today'),
    upcoming: t('common.statusUpcoming'),
  }

  useEffect(() => {
    if(contact) {
      setScopes(contact.scope? [contact.scope] : [Scope.PRIVATE]);
      setSearchText(contact.fullName || '');
    }
  }, [contact?.id])

  const handleAddReminder = () => {
    if(isCreationStackEnabled) {
      showKeepContactCreation({ contactId });
    } else {
      navigation.navigate('AddKeep', { contactId });
    }
  };

  const handleEditReminder = (reminder: KeepContact) => {
    if(isCreationStackEnabled) {
      showKeepContactCreation({ keepContactId: reminder.id });
    } else {
      navigation.navigate('AddKeep', { keepContactId: reminder.id });
    }
  };

  const handleDeleteReminder = () => {
    if (reminderIdToDelete) {
      deleteKeepContact({ id: reminderIdToDelete });
      setReminderIdToDelete(null)
    }
  };

  const showDeleteModal = (reminderId: string) => {
    setIsDeleteModalVisible(true)
    setReminderIdToDelete(reminderId)
  }

  const renderReminderItem = (reminder: KeepContact) => {
    const status = getReminderStatus(reminder.keepDate)
    const statusColor = statusColors[status]

    return (
      <View key={reminder.id} style={styles.reminderCard}>
        <TouchableOpacity  style={styles.reminderTouchable} onPress={() => handleEditReminder(reminder)}>
          <View style={styles.reminderHeader}>
            <Text style={styles.reminderDate}>{formatReminderRepeatLabel(reminder, t)}</Text>
            <View style={[styles.statusBadge, { borderColor: statusColor }]}>
              <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
              <Text style={[styles.statusText, { color: statusColor }]}>{statusLabels[status]}</Text>
            </View>
          </View>
          <View style={commonStyles.row}>
            <View style={styles.reminderContent}>
              <TouchableOpacity onPress={() => (navigation as any).navigate('Profile', { screen: 'DetailsGhostProfile', params: { profileId: reminder.contactAccountId } })}>
                <Image source={reminder.contact.avatarUrl? { uri: reminder.contact.avatarUrl } : defaultImage} style={styles.contactAvatar} />
              </TouchableOpacity>
              <View style={styles.reminderInfo}>
                <Text style={styles.contactName}>{reminder.contact.firstName} {reminder.contact.lastName}</Text>
                <Text style={styles.reminderDescription}>{reminder.name}</Text>
              </View>
            </View>
            <View style={styles.actionButton}>
              <Icon name="editPen" size={theme.iconSize.small} color={theme.colors.greyBlue} />
              <Icon name="trash" size={theme.iconSize.small} color={theme.colors.error} onPress={() => showDeleteModal(reminder.id)} />
            </View>
          </View>
        </TouchableOpacity>
      </View>
    )
  }

  const renderSection = (key: string, items: KeepContact[]) => {
    if (!isDesktop && items.length === 0) return null;

    return (
      <>
        <View style={styles.dateSection}>
          <View style={styles.dateLine} />
            <Text style={styles.dateText}>{key === 'overdue' ? t('keepContact.statusLate') : momentFilterMap[key]}</Text>
          <View style={styles.dateLine} />
        </View>
        {items.map(renderReminderItem)}
      </>
    );
  };
  

  return (
    <SafeAreaView style={styles.container}>
      <View style={commonStyles.flex}>
        {(!!contact || isExtension) && <BackButtonWeb title={t('keepContact.backToProfile')} />}
        <Header
          title={t('keepContact.pageTitle')}
          iconLeft={isWeb? undefined : "arrowBack"}
          iconRight={"plus"}
          onPressRight={handleAddReminder}
        />
        <FeatureTooltip
          id="keep_contact"
          title={t('featureTooltip.keep_contact.title')}
          description={t('featureTooltip.keep_contact.description')}
          icon="keepContact"
        />
        <View style={isMobile? styles.searchContainerMobile : styles.searchContainerWeb}>
          <View style={[styles.searchContainer, !isMobile && commonStyles.flex, !isMobile && commonStyles.searchContainerMaxWidth]}>
            <SearchBar value={searchText} onChangeText={setSearchText} />
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filtersContainer}>
            <ContactsFilter scope={scopes} onScopeChange={setScopes} noGhost />
            <TouchableOpacity
              style={[styles.filterChip, selectedHashtags.length > 0 && styles.filterChipActive]}
              onPress={() => { setPendingHashtags(selectedHashtags); setIsHashtagModalVisible(true) }}
            >
              <Icon name="htag" size={14} color={selectedHashtags.length > 0 ? theme.colors.primary : theme.colors.greyBlue} />
              <Text style={[styles.filterChipText, selectedHashtags.length > 0 && styles.filterChipTextActive]}>
                {t('filters.hashtag')}{selectedHashtags.length > 0 ? ` (${selectedHashtags.length})` : ''}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.filterChip, selectedCategories.length > 0 && styles.filterChipActive]}
              onPress={() => { setPendingCategories(selectedCategories); setIsCategoryModalVisible(true) }}
            >
              <Icon name="category" size={14} color={selectedCategories.length > 0 ? theme.colors.primary : theme.colors.greyBlue} />
              <Text style={[styles.filterChipText, selectedCategories.length > 0 && styles.filterChipTextActive]}>
                {t('filters.category')}{selectedCategories.length > 0 ? ` (${selectedCategories.length})` : ''}
              </Text>
            </TouchableOpacity>
            <MomentFilter filter={momentFilter} setFilter={setMomentFilter} />
          </ScrollView>
        </View>

        <View style={styles.viewModeRow}>
          <Text style={styles.resultsCount}>{totalCount} {totalCount > 1 ? t('keepContact.reminders') : t('keepContact.reminder')}</Text>
          <View style={styles.viewModeContainer}>
            <TouchableOpacity
              style={[styles.viewModeButton, viewMode === 'list' && styles.viewModeButtonActive]}
              onPress={() => setViewMode('list')}
            >
              <Icon name="menu" size={16} color={viewMode === 'list' ? theme.colors.primary : theme.colors.greyBlue} />
              <Text style={[styles.viewModeText, viewMode === 'list' && styles.viewModeTextActive]}>
                {t('keepContact.listView')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.viewModeButton, viewMode === 'board' && styles.viewModeButtonActive]}
              onPress={() => setViewMode('board')}
            >
              <Icon name="category" size={16} color={viewMode === 'board' ? theme.colors.primary : theme.colors.greyBlue} />
              <Text style={[styles.viewModeText, viewMode === 'board' && styles.viewModeTextActive]}>
                {t('keepContact.boardView')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {viewMode === 'board' ? (
          <BoardView keepContacts={keepContacts} onEditReminder={handleEditReminder} t={t} />
        ) : (
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {momentFilter.id !== 'default' && renderSection(momentFilter.id, keepContacts)}
            {momentFilter.id === 'default' &&
              <View style={isDesktop ? styles.contentListWeb : styles.contentList}>
                <View style={isDesktop && styles.content}>
                  {/* Late follow-ups come first; no empty « En retard » title on desktop. */}
                  {groupedByMoment.overdue.length > 0 && renderSection('overdue', groupedByMoment.overdue)}
                  {renderSection('today', groupedByMoment.today)}
                  {renderSection('week', groupedByMoment.week)}
                </View>
                {isDesktop && <View style={styles.vSpacer} />}
                <View style={isDesktop && styles.content}>
                  {renderSection('month', groupedByMoment.month)}
                  {renderSection('later', groupedByMoment.later)}
                </View>
              </View>}
          </ScrollView>
        )}
      </View>
      <GenericSelectionModal
        isVisible={isHashtagModalVisible}
        setIsVisible={setIsHashtagModalVisible}
        title={t('filters.searchHashtags')}
        placeholder={t('filters.searchHashtag')}
        availableItems={hashtags}
        selectedItems={selectedHashtags}
        onSelectionChange={setPendingHashtags}
        onClosed={() => setSelectedHashtags(pendingHashtags)}
        multiSelect
        isHashtagsModal
        fitParent={!isMobile}
        isFetching={isFetchingHashtags}
        isFetchingNextPage={isFetchingNextPageHashtags}
        hasNextPage={hasNextPageHashtags}
        fetchNextPage={fetchNextPageHashtags}
        onSearch={(search) => setFilterSearchText(search)}
      />
      <GenericSelectionModal
        isVisible={isCategoryModalVisible}
        setIsVisible={setIsCategoryModalVisible}
        title={t('filters.searchCategories')}
        placeholder={t('filters.searchCategory')}
        availableItems={categories.map(category => ({ id: category.id, name: category.name, photo: category.avatarUrl || '' }))}
        selectedItems={selectedCategories}
        onSelectionChange={setPendingCategories}
        onClosed={() => setSelectedCategories(pendingCategories)}
        multiSelect
        isCategoriesModal
        fitParent={!isMobile}
        isFetching={isFetchingCategories}
        isFetchingNextPage={isFetchingNextPageCategories}
        hasNextPage={hasNextPageCategories}
        fetchNextPage={fetchNextPageCategories}
        onSearch={(search) => setFilterSearchText(search)}
      />
      <DeleteModal isVisible={isDeleteModalVisible} setIsVisible={setIsDeleteModalVisible} title={t('keepContact.deleteConfirm')} onDelete={handleDeleteReminder} />
    </SafeAreaView>
  );
};

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'row',
      backgroundColor: theme.colors.background,
    },
    searchContainerMobile: {
      marginBottom: theme.spacing.md,
    },
    searchContainerWeb: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.lg,
    },
    searchContainer: {
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.md,
    },
    vSpacer: {
      height: '100%',
      width: 1,
      backgroundColor: theme.colors.lightGrey,
    },
    filtersContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.md,
      paddingBottom: theme.spacing.md,
    },
    viewModeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingRight: theme.spacing.lg,
      paddingBottom: theme.spacing.sm,
    },
    resultsCount: {
      fontSize: theme.fontSize.extraSmall,
      color: theme.colors.greyBlue,
      paddingHorizontal: theme.spacing.lg,
    },
    viewModeContainer: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
    },
    viewModeButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      gap: 6,
    },
    viewModeButtonActive: {
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.primary + '10',
    },
    viewModeText: {
      fontSize: 13,
      color: theme.colors.greyBlue,
    },
    viewModeTextActive: {
      color: theme.colors.primary,
      fontWeight: '600',
    },
    contentList: {
      flex: 1,
    },
    contentListWeb: {
      flex: 1,
      flexDirection: 'row', 
      gap: theme.spacing.lg,
    },
    content: {
      flex: 1,
    },
    dateSection: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      backgroundColor: theme.colors.background,
    },
    dateLine: {
      flex: 1,
      height: 1,
      backgroundColor: theme.colors.lightGrey,
    },
    dateText: {
      paddingHorizontal: theme.spacing.md,
      fontSize: theme.fontSize.small,
      color: theme.colors.secondary,
    },
    reminderCard: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      marginHorizontal: theme.spacing.md,
      marginBottom: theme.spacing.sm,
      paddingVertical: theme.spacing.sm,
      paddingLeft: theme.spacing.md,
      paddingRight: theme.spacing.xs,
      borderRadius: 12,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.06,
            shadowRadius: 3,
            elevation: 2,
          }),
    },
    reminderTouchable: {
      flex: 1,  
      minWidth: 0,
    },
    reminderHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.md,
    },
    reminderDate: {
      fontSize: theme.fontSize.extraSmall,
      color: theme.colors.primary,
      fontWeight: '500',
    },
    statusBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 12,
      borderWidth: 1,
    },
    statusDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
    },
    statusText: {
      fontSize: 11,
      fontWeight: '600',
    },
    actionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.lg,
      padding: theme.spacing.xs,
      marginLeft: theme.spacing.xs,
    },
    reminderContent: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      flex: 1,
    },
    contactAvatar: {
      width: 44,
      height: 44,
      borderRadius: 22,
      marginRight: theme.spacing.md,
    },
    reminderInfo: {
      flex: 1,
      minWidth: 0,
    },
    contactName: {
      fontSize: theme.fontSize.medium,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 2,
    },
    reminderDescription: {
      fontSize: theme.fontSize.small,
      color: theme.colors.greyBlue,
    },
    reminderActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    iconButton: {
      padding: theme.spacing.sm,
      borderRadius: 8,
    },
    deleteIconButton: {
      // distinct touch target, color on icon
    },
    filterChip: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      marginRight: 8,
      gap: 4,
    },
    filterChipActive: {
      borderColor: theme.colors.primary,
    },
    filterChipText: {
      fontSize: 14,
      color: theme.colors.greyBlue,
    },
    filterChipTextActive: {
      color: theme.colors.primary,
      fontWeight: 'bold',
    },
  }); 
